import useMeasure from "react-use-measure"
import { mergeRefs } from "../../utils/mergeRefs"
import { ResizeObserver } from "@juggle/resize-observer"
import { noop } from "lodash-es"
import { createContext, useContext, useState, useCallback, useEffect, useRef, UIEvent } from "react"
import { TableContextProviderProps, TableContextValue } from "./types"

const TableContext = createContext<TableContextValue>({
  afterColumnHeadersTop: 0,
  afterColumnHeadersRef: noop,
  firstColumnHeaderRef: noop,
  multiRowSelectOptionsConfig: [],
  onChangeMultiSelectionState: noop,
  onTableContainerScroll: noop,
  isScrollable: false,
  isSelectable: false,
  selectAllCheckboxState: "UNCHECKED",
  selectLabel: "Select all",
  tableContainerRef: noop,
  lastLoadedTableContainerHeight: 0,
})

export const TableContextProvider = ({
  children,
  fixedEndColumn,
  fixedStartColumn,
  isLoading,
  isSelectable,
  multiSelectionState,
  multiRowSelectOptionsConfig,
  onChangeMultiSelectionState,
}: TableContextProviderProps) => {
  // Utils to display afterColumnHeaders element
  const [afterColumnHeadersRef, { height: afterColumnHeadersHeight }] = useMeasure({
    polyfill: ResizeObserver,
  })

  // Fixed columns utils
  const [isScrollable, setIsScrollable] = useState<undefined | boolean>(undefined)
  const [firstColumnHeaderRef, { height: headerHeight, width: firstColumnWidth }] = useMeasure({
    polyfill: ResizeObserver,
  })
  const [
    tableContainerUseMeasureRef,
    { width: tableContainerWidth, height: tableContainerHeight },
  ] = useMeasure({
    polyfill: ResizeObserver,
  })
  const setTableContainerScrollState = useCallback<(element: HTMLDivElement | null) => void>(
    element => {
      if (!element) {
        setIsScrollable(undefined)
        return
      }

      setIsScrollable?.(element.scrollWidth > element.clientWidth)
    },
    [],
  )
  const onTableContainerScroll = useCallback(
    ({ target }: UIEvent<HTMLDivElement>) => setTableContainerScrollState(target as HTMLDivElement),
    [setTableContainerScrollState],
  )

  // We need this separate ref since useMeasure uses a functional ref, and we need to maintain access to that ref
  const internalTableContainerRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    // set it when the element resizes
    setTableContainerScrollState(internalTableContainerRef.current || null)
  }, [setTableContainerScrollState, tableContainerWidth])

  // Merge so that the we only apply one ref function to the element
  const tableContainerRef = mergeRefs(internalTableContainerRef, tableContainerUseMeasureRef)

  // When the table is not loading and showing data, take a measurement.
  // We use this measurement when the table is next loading so that the table height doesn't jump
  const lastLoadedHeight = useRef(0)
  useEffect(() => {
    if (!isLoading) {
      lastLoadedHeight.current = tableContainerHeight
    }
  }, [isLoading, tableContainerHeight])

  // Row multi-selection utils
  const currentOption = multiRowSelectOptionsConfig.find(({ key }) => key === multiSelectionState)
  const selectLabel = currentOption?.label || "Select all"
  const selectAllCheckboxState: "UNCHECKED" | "CHECKED" | "INDETERMINATE" = currentOption
    ? currentOption.checked
    : multiSelectionState === "SOME"
      ? "INDETERMINATE"
      : "UNCHECKED"

  return (
    <TableContext.Provider
      value={{
        isSelectable,
        onChangeMultiSelectionState,
        firstColumnHeaderRef,
        firstColumnWidth,
        fixedStartColumn,
        fixedEndColumn,
        onTableContainerScroll,
        isLoading,
        isScrollable,
        afterColumnHeadersHeight,
        afterColumnHeadersTop: (headerHeight ?? 0) - (afterColumnHeadersHeight ?? 0),
        afterColumnHeadersRef,
        selectAllCheckboxState,
        selectLabel,
        multiRowSelectOptionsConfig,
        tableContainerRef,
        lastLoadedTableContainerHeight: lastLoadedHeight.current,
      }}
    >
      {children}
    </TableContext.Provider>
  )
}

export const useTableContext = () => useContext(TableContext)
